import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Button,
  SimpleGrid,
  useDisclosure,
  useToast,
  Text,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  Input,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardBody,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Badge,
  Select,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tooltip,
  InputRightElement,
} from '@chakra-ui/react';
import { FiPlus, FiSearch, FiFilter, FiDownload, FiEdit2, FiTrash2 } from 'react-icons/fi';
import ClubCard from '../components/ClubCard';
import NewClubForm from '../components/NewClubForm';
import EditClubForm from '../components/EditClubForm';
import PageLayout from '../components/PageLayout';
import { useNavigate } from 'react-router-dom';
import {
  fetchClubs,
  addClub,
  editClub,
  removeClub,
  selectAllClubs,
  selectClubStatus,
  selectClubError,
  setSelectedClub,
  selectSelectedClub,
  rejectClub,
} from '../store/slices/clubSlice';
import { useAuth } from '../context/AuthContext';
import { getClubs, createClub as apiCreateClub, updateClub as apiUpdateClub, deleteClub as apiDeleteClub } from '../services/api';
import StatCard from '../components/StatCard';
import { EditIcon, DeleteIcon, AddIcon } from '@chakra-ui/icons';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';

const ClubManagement = () => {
  const dispatch = useDispatch();
  const clubs = useSelector(selectAllClubs);
  const status = useSelector(selectClubStatus);
  const error = useSelector(selectClubError);
  const selectedClub = useSelector(selectSelectedClub);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const { isOpen: isNewClubOpen, onOpen: onNewClubOpen, onClose: onNewClubClose } = useDisclosure();
  const { isOpen: isEditClubOpen, onOpen: onEditClubOpen, onClose: onEditClubClose } = useDisclosure();
  const { isOpen: isDeleteAlertOpen, onOpen: onDeleteAlertOpen, onClose: onDeleteAlertClose } = useDisclosure();
  const [clubToDelete, setClubToDelete] = useState(null);
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const cancelRef = React.useRef();
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('gray.900', 'gray.700');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filteredClubs, setFilteredClubs] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  // Memoized fetch function to prevent infinite loops
  const fetchClubsData = useCallback(async () => {
    try {
      const response = await getClubs();
      const data = response?.data || [];
      dispatch(fetchClubs(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Failed to fetch users:', error);
      dispatch(fetchClubs([]));
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to fetch users',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [dispatch, toast]);

  // Initial data fetch
  useEffect(() => {
    fetchClubsData();
  }, [fetchClubsData]);

  // Error handling effect
  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: error,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error, toast]);

  // Filter clubs effect
  useEffect(() => {
    if (Array.isArray(clubs)) {
      const filtered = clubs.filter(club => {
        if (!club || !club.name) return false;
        
        const searchLower = searchQuery.toLowerCase();
        const nameLower = club.name.toLowerCase();
        const descriptionLower = club.description?.toLowerCase() || '';
        
        const matchesSearch = nameLower.includes(searchLower) || 
                            descriptionLower.includes(searchLower);
        const matchesStatus = filterStatus === 'all' || club.status === filterStatus;
        
        return matchesSearch && matchesStatus;
      });
      setFilteredClubs(filtered);
    } else {
      setFilteredClubs([]);
    }
  }, [clubs, searchQuery, filterStatus]);

  const handleNewClub = async (clubData) => {
    try {
      // Show loading toast
      const loadingToast = toast({
        title: 'Creating user...',
        status: 'info',
        duration: null,
        isClosable: false,
      });

      const response = await apiCreateClub(clubData);
      
      if (!response?.data) {
        throw new Error('Invalid response from server');
      }

      const newClub = response.data;
      
      // Update the Redux store
      dispatch(addClub(newClub));
      
      // Close the loading toast
      toast.close(loadingToast);
      
      // Show success toast
      toast({
        title: 'Success',
        description: 'New user has been created successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Close the modal
      onNewClubClose();
      
      // Refresh the clubs list
      await fetchClubsData();
    } catch (error) {
      console.error('Error creating user:', error);
      
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message || 'Failed to create user',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      
      // Don't close the modal on error
      // This allows the user to fix any issues and try again
    }
  };

  const handleEditClub = async (clubData) => {
    if (!clubData?._id) {
      toast({
        title: 'Error',
        description: 'Invalid user data. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Show loading toast
      const loadingToast = toast({
        title: 'Updating user...',
        status: 'info',
        duration: null,
        isClosable: false,
      });

      // Ensure we're passing the ID correctly
      const response = await dispatch(editClub({ 
        id: clubData._id, 
        clubData: clubData 
      })).unwrap();
      
      // Close the loading toast
      toast.close(loadingToast);
      
      // Show success toast
      toast({
        title: 'Success',
        description: 'user has been updated successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Close the modal and clear selected club
      onEditClubClose();
      dispatch(setSelectedClub(null));
      
      // Refresh the clubs list
      await fetchClubsData();
    } catch (error) {
      console.error('Error updating user:', error);
      
      // Show error toast with specific message
      toast({
        title: 'Error updating user',
        description: error.message || 'Failed to update user. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteClub = async (id) => {
    try {
      if (!id) {
        throw new Error('user ID is required');
      }

      // First dispatch the action to update UI optimistically
      dispatch(removeClub(id));

      // Then make the API call
      const response = await apiDeleteClub(id);

      if (!response?.data) {
        throw new Error('Failed to delete user');
      }

      toast({
        title: 'Success',
        description: 'user has been deleted successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onDeleteAlertClose();

      // Refresh the clubs list
      fetchClubsData();
    } catch (error) {
      console.error('Error deleting user:', error);

      // Revert the optimistic update
      fetchClubsData();

      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message || 'Failed to delete club',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleClubClick = (club) => {
    if (!club?._id) {
      toast({
        title: 'Error',
        description: 'Invalid user data. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    dispatch(setSelectedClub(club));
    onEditClubOpen();
  };

  const handleManageKnowledgeBase = (club) => {
    navigate(`/knowledge-base/${club._id}`);
  };

  const handleManageWidgets = (club) => {
    // Implementation needed
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await fetchClubsData();
  };

  const handleCreate = async (newClub) => {
    try {
      await dispatch(addClub(newClub)).unwrap();
      toast({
        title: 'user created',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onCreateClose();
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Error creating club';
      toast({
        title: 'Error creating user',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUpdate = async (updatedClub) => {
    try {
      await dispatch(editClub({ id: selectedClub._id, clubData: updatedClub })).unwrap();
      toast({
        title: 'Club updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onEditClubClose();
    } catch (error) {
      toast({
        title: 'Error updating club',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const stats = {
    total: clubs.length,
    active: clubs.filter(club => club.status === 'active').length,
    inactive: clubs.filter(club => club.status === 'inactive').length,
    totalMembers: clubs.reduce((sum, club) => sum + (club.memberCount || 0), 0),
  };

  if (status === 'loading') {
    return (
      <PageLayout>
        <Box p={4}>
          <VStack spacing={4} align="center">
            <Spinner size="xl" />
            <Text>Loading users...</Text>
          </VStack>
        </Box>
      </PageLayout>
    );
  }

  if (status === 'failed') {
    return (
      <PageLayout>
        <Box p={4}>
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error loading users</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Box p={4}>
        <VStack spacing={6} align="stretch">
          {/* Header Section */}
          <HStack justify="space-between" align="center">
            <Heading size="lg">user Management</Heading>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="teal"
              onClick={onCreateOpen}
            >
              Create New user
            </Button>
          </HStack>

          {/* Stats Section */}
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={6}>
            <StatCard label="Total users" value={stats.total} help="All registered users" />
            <StatCard label="Active users" value={stats.active} help="Currently active" />
            <StatCard label="Inactive users" value={stats.inactive} help="Currently inactive" />
            <StatCard label="Total Members" value={stats.totalMembers} help="Across all users" />
          </SimpleGrid>

          {/* Search and Filter Section */}
          <HStack spacing={4}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <FiSearch color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={handleInputChange}
                bg={bgColor}
                borderColor={borderColor}
              />
            </InputGroup>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              bg={bgColor}
              borderColor={borderColor}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </Select>
          </HStack>

          {/* Clubs Grid */}
          {filteredClubs.length === 0 ? (
            <Box
              p={8}
              textAlign="center"
              bg={bgColor}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
            >
              <Text fontSize="lg" color="gray.500">
                No clubs found. Click "Create New user" to create one.
              </Text>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {filteredClubs.map((club) => (
                <ClubCard
                  key={club._id}
                  club={club}
                  onEdit={() => handleClubClick(club)}
                  onDelete={() => {
                    setClubToDelete(club);
                    onDeleteAlertOpen();
                  }}
                  onManageKnowledgeBase={() => handleManageKnowledgeBase(club)}
                  onManageWidgets={() => handleManageWidgets(club)}
                />
              ))}
            </SimpleGrid>
          )}
        </VStack>

        {/* Edit Club Modal */}
        <Modal isOpen={isEditClubOpen} onClose={onEditClubClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit Club</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {selectedClub && (
                <>
                  <EditClubForm
                    club={selectedClub}
                    onSubmit={handleEditClub}
                    onCancel={onEditClubClose}
                  />
                  <FormControl>
                    <FormLabel>User Email</FormLabel>
                    <Input value={selectedClub.userEmail || ''} isReadOnly />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Original Password</FormLabel>
                    <InputGroup>
                      <Input
                        value={selectedClub.userPassword || ''}
                        isReadOnly
                        type={showPassword ? 'text' : 'password'}
                      />
                      <InputRightElement width="4.5rem">
                        <Button h="1.75rem" size="sm" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                </>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          isOpen={isDeleteAlertOpen}
          leastDestructiveRef={cancelRef}
          onClose={onDeleteAlertClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Club
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to delete this club? This action cannot be undone.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDeleteAlertClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={() => {
                    if (clubToDelete) {
                      handleDeleteClub(clubToDelete._id);
                    }
                  }}
                  ml={3}
                >
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* Create Club Modal */}
        <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Create New Club</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <NewClubForm
                onSubmit={handleCreate}
                onCancel={onCreateClose}
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </PageLayout>
  );
};

export default ClubManagement;